import { getSupabaseClient } from "./supabase";
import { getResolvedWorkspace } from "./workspaceConfig";

/**
 * Calls apps/services' OpenAI relay (decision #18's "OpenAI via server
 * proxy" leg - OpenAI's API blocks direct browser calls, unlike
 * Anthropic's) as the signed-in tenant user, same session-token pattern
 * callNylasApi uses. byoai.ts is the only caller. Mirrors the Chat
 * Completions wire format (not our own simplification) so tool round
 * trips - assistant tool_calls, tool-role results - pass through untouched.
 */
export type OpenAiRole = "system" | "user" | "assistant" | "tool";

export interface OpenAiToolCall {
  id: string;
  type: "function";
  function: { name: string; arguments: string };
}

export interface OpenAiMessage {
  role: OpenAiRole;
  content?: string | null;
  tool_calls?: OpenAiToolCall[];
  tool_call_id?: string;
}

export interface OpenAiToolDef {
  type: "function";
  function: {
    name: string;
    description?: string;
    parameters?: Record<string, unknown>;
  };
}

export interface OpenAiChatResult {
  content: string | null;
  toolCalls?: OpenAiToolCall[];
}

export async function callOpenAiChat(params: {
  apiKey: string;
  model?: string;
  messages: OpenAiMessage[];
  tools?: OpenAiToolDef[];
}): Promise<OpenAiChatResult> {
  const servicesUrl = (import.meta.env.VITE_CONTROL_PLANE_URL ?? "").replace(/\/+$/, "");
  const slug = getResolvedWorkspace()?.slug;
  if (!servicesUrl || !slug) {
    throw new Error("AI chat needs a deployed workspace - it isn't available in this preview.");
  }

  const { data } = await getSupabaseClient().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Not signed in.");

  const query = new URLSearchParams({ tenant: slug });
  const res = await fetch(`${servicesUrl}/integrations/ai/openai/chat?${query.toString()}`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${token}`,
      "content-type": "application/json",
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const reason = await res
      .json()
      .then((b: { reason?: string }) => b.reason)
      .catch(() => undefined);
    throw new Error(reason ?? `Request failed (${res.status}).`);
  }
  return (await res.json()) as OpenAiChatResult;
}
